import type { OnboardingTemplate, OnboardingTemplateStep } from 'src/interfaces/onboardingTemplate'
import { normalizeCollectDataTemplateConfigId, resolveStepGroupConfigId } from './configIds'

/**
 * Collect-data config group order shown in the flow editor sidebar.
 * Steps-first (matches flat `editorSteps` block order), then
 * template configs that have at least one editor step (server order), then client-only empty groups.
 */
export function buildFlowEditorSidebarOrderedConfigIds(
  template: OnboardingTemplate,
  editorSteps: OnboardingTemplateStep[],
  emptyGroupUiIds: string[],
): string[] {
  const seen = new Set<string>()
  const orderedIds: string[] = []

  for (const step of editorSteps) {
    const groupConfigId = resolveStepGroupConfigId(step, template)

    if (groupConfigId === '' || seen.has(groupConfigId)) {
      continue
    }

    seen.add(groupConfigId)
    orderedIds.push(groupConfigId)
  }

  const configs = [...(template.collectDataTemplateStepsConfigs ?? [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  )

  for (const stepsConfig of configs) {
    const id = normalizeCollectDataTemplateConfigId(stepsConfig.id) || stepsConfig.id

    if (id === '' || seen.has(id)) {
      continue
    }

    const hasEditorSteps = editorSteps.some(
      (step) => resolveStepGroupConfigId(step, template) === id,
    )

    if (!hasEditorSteps) {
      continue
    }

    seen.add(id)
    orderedIds.push(id)
  }

  for (const emptyGroupId of emptyGroupUiIds) {
    const normalizedEmptyGroupId =
      normalizeCollectDataTemplateConfigId(emptyGroupId) || emptyGroupId

    if (normalizedEmptyGroupId === '' || seen.has(normalizedEmptyGroupId)) {
      continue
    }

    seen.add(normalizedEmptyGroupId)
    orderedIds.push(normalizedEmptyGroupId)
  }

  return orderedIds
}

/**
 * Default flow-editor group title: `Step group #n` where `n` is the 1-based index in
 * {@link buildFlowEditorSidebarOrderedConfigIds} for `configId`.
 */
export function flowEditorStepGroupDefaultLabel(
  template: OnboardingTemplate,
  editorSteps: OnboardingTemplateStep[],
  emptyGroupUiIds: string[],
  configId: string,
): string {
  const ordered = buildFlowEditorSidebarOrderedConfigIds(template, editorSteps, emptyGroupUiIds)
  const key = normalizeCollectDataTemplateConfigId(configId) || configId
  const idx = ordered.findIndex((id) => (normalizeCollectDataTemplateConfigId(id) || id) === key)

  if (idx < 0) {
    return `Step group ${ordered.length + 1}`
  }

  return `Step group ${idx + 1}`
}

/**
 * When non-null, save must be blocked: at least one sidebar step group has no steps.
 */
export function getFlowEditorEmptyStepGroupSaveMessage(
  template: OnboardingTemplate,
  editorSteps: OnboardingTemplateStep[],
  emptyGroupUiIds: string[],
): string | null {
  const orderedIds = buildFlowEditorSidebarOrderedConfigIds(template, editorSteps, emptyGroupUiIds)

  for (const configId of orderedIds) {
    const stepCount = editorSteps.filter(
      (step) => resolveStepGroupConfigId(step, template) === configId,
    ).length

    if (stepCount === 0) {
      return 'Every step group must include at least one step before you can save.'
    }
  }

  return null
}
